<template>
  <div class="psp">
    <button type="button" class="psp__filter-mob" @click="mobileFiltersOpen = true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M4 6h16M4 12h10M4 18h16"/>
      </svg>
      Фильтры
      <span v-if="activeFilterCount" class="psp__filter-badge">{{ activeFilterCount }}</span>
    </button>

    <section class="psp-filters psp-filters--desktop">
      <ProgramsFilterPanel
        :ui="ui"
        :filters="filters"
        :gpa-slider="gpaSlider"
        :ielts-slider="ieltsSlider"
        :has-active-filters="hasActiveFilters"
        @toggle-multi="onToggleMulti"
        @gpa-change="onGpaSliderChange"
        @ielts-change="onIeltsSliderChange"
        @duolingo="setDuolingo"
        @clear="clearFilters"
        @remove-pill="onRemovePill"
      />
    </section>

    <section class="psp__search-section" aria-label="Поиск программ">
      <label class="psp__search-label" for="psp-search-input">Поиск программ</label>
      <div class="psp__toolbar">
        <div class="psp__search">
          <svg class="psp__search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            id="psp-search-input"
            v-model="filters.search"
            type="search"
            placeholder="Название программы или университета…"
            @keyup.enter="runSearchNow"
          />
        </div>

        <div
          v-if="ui?.ranks?.length"
          ref="rankFilterRef"
          class="psp__rank"
          :class="{ 'psp__rank--open': rankDropdownOpen }"
        >
        <button
          type="button"
          class="psp__rank-trigger"
          :class="{ 'psp__rank-trigger--active': filters.rankIds.length }"
          @click="rankDropdownOpen = !rankDropdownOpen"
        >
          Рейтинг
          <span v-if="filters.rankIds.length" class="psp__rank-badge">{{ filters.rankIds.length }}</span>
          <svg class="psp__rank-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        <div v-if="rankDropdownOpen" class="psp__rank-menu">
          <button
            v-for="opt in ui.ranks"
            :key="opt.id"
            type="button"
            class="psp__rank-chip"
            :class="{
              'psp__rank-chip--active': filters.rankIds.includes(opt.id),
              'psp__rank-chip--disabled': opt.disabled && !filters.rankIds.includes(opt.id),
            }"
            :disabled="opt.disabled && !filters.rankIds.includes(opt.id)"
            @click="onRankToggle(opt.id)"
          >
            {{ opt.name }}<span v-if="!filters.rankIds.includes(opt.id)" class="psp__rank-chip__count">{{ opt.count }}</span>
          </button>
        </div>
        </div>
      </div>
    </section>

    <!-- Results -->
    <section class="psp-results">
      <div class="psp-results__head">
        <p v-if="!loading && ui" class="psp-results__count">
          Найдено: <strong>{{ ui.meta.total }}</strong>
          {{ programsCountLabel }}
        </p>
        <p v-else-if="loading" class="psp-results__count psp-results__count--skeleton" aria-hidden="true">
          <span class="sk sk--text" style="display: inline-block; width: 180px; height: 16px;" />
        </p>
      </div>

      <div v-if="loading" class="psp-block">
        <div v-for="n in 4" :key="n" class="psp-block__in">
          <div class="psp-skeleton" />
        </div>
      </div>

      <div v-else-if="programs.length" class="psp-block">
        <div v-for="item in programs" :key="item.id" class="psp-block__in">
          <newCardComponent :item="item" @newFav="runSearchNow" />
        </div>
      </div>

      <div v-else class="psp-empty">
        <div class="psp-empty__icon">🔍</div>
        <p class="psp-empty__title">Программы не найдены</p>
        <p class="psp-empty__text">Попробуйте изменить фильтры или запрос</p>
        <button v-if="hasActiveFilters" type="button" class="psp-empty__btn" @click="clearFilters">
          Сбросить фильтры
        </button>
      </div>

      <div v-if="!loading && ui && ui.meta.total > filters.perPage" class="psp-paginate">
        <vue-awesome-paginate
          v-model="filters.page"
          :total-items="ui.meta.total"
          :items-per-page="filters.perPage"
          @click="onPageChange"
        />
      </div>
    </section>

    <!-- Mobile filters sheet -->
    <teleport to="body">
      <div v-if="mobileFiltersOpen" class="psp-sheet-backdrop" @click="mobileFiltersOpen = false">
        <div class="psp-sheet" @click.stop>
          <div class="psp-sheet__handle" />
          <h2 class="psp-sheet__title">Фильтры</h2>
          <div class="psp-sheet__body">
            <ProgramsFilterPanel
              :ui="ui"
              :filters="filters"
              :gpa-slider="gpaSlider"
              :ielts-slider="ieltsSlider"
              :has-active-filters="hasActiveFilters"
              @toggle-multi="onToggleMulti"
              @gpa-change="onGpaSliderChange"
              @ielts-change="onIeltsSliderChange"
              @duolingo="setDuolingo"
              @clear="clearFilters"
              @remove-pill="onRemovePill"
            />
          </div>
          <button type="button" class="psp-sheet__apply" @click="applyMobileFilters">
            Показать результаты
          </button>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import newCardComponent from '@/components/newCardComponent.vue';
import ProgramsFilterPanel from '@/components/programs/ProgramsFilterPanel.vue';
import {
  defaultFilterState,
  loadFilterCatalog,
  mergeFacetsIntoUi,
  searchUniversityPrograms,
  isGpaActive,
  isIeltsActive,
  normalizeGpaRange,
  filtersToQuery,
  filtersFromQuery,
  sanitizeRestoredRange,
  EXTERNAL_SCHOLARSHIP_IDS,
  openExternalScholarship,
} from '@/utils/programSearch';

function sameQuery(a, b) {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => String(a[k]) === String(b[k]));
}

export default {
  name: 'ProgramsSearchPage',
  components: { newCardComponent, ProgramsFilterPanel },
  data() {
    return {
      catalog: null,
      ui: null,
      // Restore filters from the URL query so they survive list -> detail -> back navigation.
      filters: filtersFromQuery(this.$route?.query),
      gpaSlider: { min: 0, max: 4, from: 0, to: 4 },
      ieltsSlider: { min: 5, max: 9, from: 5, to: 9 },
      loading: false,
      catalogLoading: true,
      mobileFiltersOpen: false,
      rankDropdownOpen: false,
      searchTimer: null,
      hydrating: false,
    };
  },
  computed: {
    programs() {
      return this.ui?.programs ?? [];
    },
    hasActiveFilters() {
      return this.activeFilterCount > 0 || !!this.filters.search?.trim();
    },
    activeFilterCount() {
      const f = this.filters;
      let n = 0;
      n += f.countryIds.length + f.stateIds.length + f.studyDegreeIds.length;
      n += f.scholarshipIds.length + f.categoryIds.length + f.subcategoryIds.length;
      n += f.rankIds.length;
      if (f.gpa) n += 1;
      if (f.ielts) n += 1;
      if (f.duolingo !== null) n += 1;
      return n;
    },
    programsCountLabel() {
      const n = this.ui?.meta?.total ?? 0;
      const m10 = n % 10;
      const m100 = n % 100;
      if (m10 === 1 && m100 !== 11) return 'программа';
      if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'программы';
      return 'программ';
    },
  },
  watch: {
    'filters.search'() {
      if (this.hydrating) return;
      this.filters.page = 1;
      this.scheduleSearch(300);
    },
    // External query change (browser back/forward within the list, menu link to /programs)
    // while the component stays mounted — rehydrate filters and refetch.
    '$route.query'(query) {
      if (this.$route.name !== 'programs') return;
      const next = filtersFromQuery(query);
      if (sameQuery(filtersToQuery(next), filtersToQuery(this.filters))) return;
      this.hydrating = true;
      this.filters = next;
      this.sanitizeRestoredRanges();
      this.syncSlidersFromFilters();
      this.$nextTick(() => {
        this.hydrating = false;
      });
      this.runSearchNow();
    },
  },
  async created() {
    await this.boot();
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    ...mapActions(['toggleBlock']),

    guardGuest() {
      if (this.$store.state.role === 'guest') {
        this.toggleBlock(true);
        return true;
      }
      return false;
    },

    async boot() {
      this.catalogLoading = true;
      try {
        const lang = localStorage.getItem('lang') || 'ru';
        this.catalog = await loadFilterCatalog(lang);
        this.sanitizeRestoredRanges();
        this.syncSlidersFromFilters();
        this.ui = mergeFacetsIntoUi(this.catalog, { facets: {}, data: [], meta: {} }, this.filters);
        await this.runSearch();
      } catch (e) {
        console.error('Failed to load program filters', e);
      } finally {
        this.catalogLoading = false;
      }
    },

    scheduleSearch(delay = 0) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.runSearch(), delay);
    },

    runSearchNow() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.runSearch();
    },

    async runSearch() {
      if (!this.catalog) return;
      this.syncRouteQuery();
      this.loading = true;
      try {
        const lang = localStorage.getItem('lang') || 'ru';
        const res = await searchUniversityPrograms(this.filters, lang);
        this.ui = mergeFacetsIntoUi(this.catalog, res, this.filters);
        this.pruneInvalidSelections();

        const catalogGpa = normalizeGpaRange(this.catalog.ranges?.gpa);
        const catalogIelts = this.catalog.ranges?.ielts ?? { min: 5, max: 9 };
        this.gpaSlider = {
          min: catalogGpa.min,
          max: catalogGpa.max,
          from: this.filters.gpa?.from ?? this.gpaSlider.from,
          to: this.filters.gpa?.to ?? this.gpaSlider.to,
        };
        this.ieltsSlider = {
          min: catalogIelts.min,
          max: catalogIelts.max,
          from: this.filters.ielts?.from ?? this.ieltsSlider.from,
          to: this.filters.ielts?.to ?? this.ieltsSlider.to,
        };
      } catch (e) {
        console.error('Program search failed', e);
      } finally {
        this.loading = false;
      }
    },

    // Mirror current filters into the URL so history keeps them for back navigation.
    syncRouteQuery() {
      const query = filtersToQuery(this.filters);
      if (sameQuery(query, this.$route.query)) return;
      this.$router.replace({ query }).catch(() => {});
    },

    sanitizeRestoredRanges() {
      if (!this.catalog) return;
      const gpaRange = normalizeGpaRange(this.catalog.ranges?.gpa);
      const ieltsRange = this.catalog.ranges?.ielts ?? { min: 5, max: 9 };
      this.filters.gpa = sanitizeRestoredRange(this.filters.gpa, gpaRange);
      this.filters.ielts = sanitizeRestoredRange(this.filters.ielts, ieltsRange);
    },

    syncSlidersFromFilters() {
      if (!this.catalog) return;
      const gpa = normalizeGpaRange(this.catalog.ranges?.gpa);
      const ielts = this.catalog.ranges?.ielts ?? { min: 5, max: 9 };
      this.gpaSlider = {
        min: gpa.min,
        max: gpa.max,
        from: this.filters.gpa?.from ?? gpa.min,
        to: this.filters.gpa?.to ?? gpa.max,
      };
      this.ieltsSlider = {
        min: ielts.min,
        max: ielts.max,
        from: this.filters.ielts?.from ?? ielts.min,
        to: this.filters.ielts?.to ?? ielts.max,
      };
    },

    onDocumentClick(e) {
      if (!this.rankDropdownOpen) return;
      const el = this.$refs.rankFilterRef;
      if (el && !el.contains(e.target)) {
        this.rankDropdownOpen = false;
      }
    },

    onRankToggle(id) {
      this.onToggleMulti({ key: 'rankIds', id });
    },

    onToggleMulti({ key, id }) {
      if (this.guardGuest()) return;

      if (key === 'scholarshipIds' && EXTERNAL_SCHOLARSHIP_IDS.includes(id)) {
        openExternalScholarship(id);
        return;
      }

      const arr = [...(this.filters[key] || [])];
      const idx = arr.indexOf(id);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(id);
      this.filters[key] = arr;

      if (key === 'countryIds') this.sanitizeStates();
      if (key === 'categoryIds') this.sanitizeSubcategories();

      this.filters.page = 1;
      this.runSearchNow();
    },

    pruneInvalidSelections() {
      if (!this.ui) return;
      if (this.ui.states?.length) {
        const allowed = new Set(this.ui.states.map((s) => s.id));
        this.filters.stateIds = this.filters.stateIds.filter((id) => allowed.has(id));
      } else if (!this.filters.countryIds.length) {
        // states hidden when no country — keep ids for when user re-selects country
      } else {
        this.filters.stateIds = [];
      }
      if (this.ui.subcategories?.length) {
        const allowed = new Set(this.ui.subcategories.map((s) => s.id));
        this.filters.subcategoryIds = this.filters.subcategoryIds.filter((id) => allowed.has(id));
      } else if (!this.filters.categoryIds.length) {
        // same as states
      } else {
        this.filters.subcategoryIds = [];
      }
    },

    sanitizeStates() {
      if (!this.catalog?.states) return;
      let states = this.catalog.states;
      if (this.filters.countryIds.length) {
        states = states.filter((s) => this.filters.countryIds.includes(s.country_id));
      }
      const allowed = new Set(states.map((s) => s.id));
      this.filters.stateIds = this.filters.stateIds.filter((id) => allowed.has(id));
    },

    sanitizeSubcategories() {
      if (!this.catalog?.subcategories) return;
      let subs = this.catalog.subcategories;
      if (this.filters.categoryIds.length) {
        subs = subs.filter((sc) => this.filters.categoryIds.includes(sc.category_id));
      }
      const allowed = new Set(subs.map((s) => s.id));
      this.filters.subcategoryIds = this.filters.subcategoryIds.filter((id) => allowed.has(id));
    },

    onGpaSliderChange({ from, to }) {
      if (this.guardGuest()) return;
      this.gpaSlider = { ...this.gpaSlider, from, to };
      const range = normalizeGpaRange(this.catalog?.ranges?.gpa);
      const gpa = { from: parseFloat(from), to: parseFloat(to) };
      this.filters.gpa = isGpaActive(gpa, range) ? gpa : null;
      this.filters.page = 1;
      this.scheduleSearch(200);
    },

    onIeltsSliderChange({ from, to }) {
      if (this.guardGuest()) return;
      this.ieltsSlider = { ...this.ieltsSlider, from, to };
      const range = this.catalog?.ranges?.ielts ?? { min: 5, max: 9 };
      const ielts = { from: parseFloat(from), to: parseFloat(to) };
      this.filters.ielts = isIeltsActive(ielts, range) ? ielts : null;
      this.filters.page = 1;
      this.scheduleSearch(200);
    },

    setDuolingo(value) {
      if (this.guardGuest()) return;
      this.filters.duolingo = value;
      this.filters.page = 1;
      this.runSearchNow();
    },

    onPageChange(page) {
      this.filters.page = page;
      this.runSearchNow();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    clearFilters() {
      this.filters = defaultFilterState();
      if (this.catalog?.ranges) {
        const gpa = normalizeGpaRange(this.catalog.ranges.gpa);
        const ielts = this.catalog.ranges.ielts ?? { min: 5, max: 9 };
        this.gpaSlider = { min: gpa.min, max: gpa.max, from: gpa.min, to: gpa.max };
        this.ieltsSlider = { min: ielts.min, max: ielts.max, from: ielts.min, to: ielts.max };
      }
      this.filters.page = 1;
      this.runSearchNow();
    },

    onRemovePill({ key, id }) {
      const arr = [...(this.filters[key] || [])];
      const idx = arr.indexOf(id);
      if (idx >= 0) arr.splice(idx, 1);
      this.filters[key] = arr;
      if (key === 'countryIds') this.sanitizeStates();
      if (key === 'categoryIds') this.sanitizeSubcategories();
      this.filters.page = 1;
      this.runSearchNow();
    },

    applyMobileFilters() {
      this.mobileFiltersOpen = false;
      this.runSearchNow();
    },
  },
};
</script>

<style lang="scss" scoped>
.psp {
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.psp__search-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.psp__search-label {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.01em;
}

.psp__toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: stretch;
}

.psp__search {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1.5px solid #d1d5db;
  border-radius: 14px;
  padding: 0 18px;
  min-height: 56px;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.07);
  color: #6b7280;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: #11415d;
    box-shadow: 0 0 0 3px rgba(17, 65, 93, 0.12), 0 2px 8px rgba(16, 24, 40, 0.07);

    .psp__search-icon {
      color: #11415d;
    }
  }

  &-icon {
    flex-shrink: 0;
    transition: color 0.15s;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 16px;
    font-weight: 500;
    font-family: inherit;
    color: #111827;
    background: transparent;

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }
  }
}

.psp__rank {
  position: relative;
  flex-shrink: 0;
}

.psp__rank-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid #e8eaed;
  border-radius: 14px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.12s, color 0.12s, background 0.12s;

  &--active {
    border-color: rgba(232, 25, 44, 0.35);
    color: #e8192c;
    background: rgba(232, 25, 44, 0.04);
  }

  &:hover {
    border-color: #d1d5db;
  }
}

.psp__rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e8192c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.psp__rank-chevron {
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.18s;

  .psp__rank--open & {
    transform: rotate(180deg);
  }
}

.psp__rank-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 50;
  min-width: 260px;
  max-width: min(360px, calc(100vw - 32px));
  padding: 12px;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.psp__rank-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1.5px solid #e5e7eb;
  background: transparent;
  color: #6b7280;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;

  &__count {
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
  }

  &:hover:not(:disabled) {
    border-color: #d1d5db;
    color: #374151;
  }

  &--active {
    border-color: #e8192c;
    background: rgba(232, 25, 44, 0.06);
    color: #e8192c;

    .psp__rank-chip__count { color: #e8192c; }
  }

  &--disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }
}

.psp__filter-mob {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #e8eaed;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: inherit;
  position: relative;
}

.psp__filter-badge {
  background: #e8192c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
}

.psp-filters--desktop {
  @media (max-width: 900px) {
    display: none;
  }
}

.psp-results__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.psp-results__count {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;

  strong { color: #111827; }
  &--muted { color: #9ca3af; }
}

.psp-block {
  display: flex;
  flex-wrap: wrap;
  gap: 2%;
  row-gap: 20px;
}

.psp-block__in {
  width: 49%;
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.psp-skeleton {
  height: 280px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f1f3 25%, #e8eaed 50%, #f0f1f3 75%);
  background-size: 200% 100%;
  animation: psp-shimmer 1.2s ease-in-out infinite;
}

@keyframes psp-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.psp-empty {
  text-align: center;
  padding: 56px 24px;
  background: #f8f9fb;
  border-radius: 16px;
  border: 1px dashed #e5e7eb;

  &__icon { font-size: 36px; margin-bottom: 12px; }
  &__title { margin: 0 0 6px; font-size: 16px; font-weight: 700; color: #111827; }
  &__text { margin: 0 0 16px; font-size: 14px; color: #9ca3af; }
  &__btn {
    padding: 10px 20px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    background: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    &:hover { background: #f9fafb; }
  }
}

.psp-paginate {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.psp-sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 200;
  display: flex;
  align-items: flex-end;
}

.psp-sheet {
  width: 100%;
  max-height: 88vh;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 12px 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.psp-sheet__handle {
  width: 46px;
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  margin: 0 auto;
}

.psp-sheet__title {
  margin: 0;
  text-align: center;
  font-size: 18px;
  font-weight: 800;
}

.psp-sheet__body {
  overflow-y: auto;
  flex: 1;
}

.psp-sheet__apply {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #0f1f3d;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

@media (max-width: 900px) {
  .psp__filter-mob { display: inline-flex; }
}
</style>
